import React, { useRef, useEffect, useState, useReducer } from "react";
import styles from "./ConflictDialog.css";
import Button from "../Button";
import { useDate, useTime } from "../Format";
import Radiobutton from "../Radiobutton";

const selectionReducer = (state, action) => {
  const item = state.filter(
    (item) =>
      item.offerId == action.payload.offer.id &&
      item.participantIndex == action.payload.participantIndex
  )[0];

  item.type = action.type;
  return [...state];
};

const ConflictDialog = function ({
  children,
  form,
  onClose,
  open,
  offers,
  dispatch,
}) {
  const modalRef = useRef(null);
  const selectionRef = useRef([]);

  const selection = form.resolve.selected.flatMap(
    (participantOffers, participantIndex) =>
      participantOffers.map((offerId) => ({
        participantIndex,
        offerId,
        type: form.participants[participantIndex]?.waitingList?.includes(
          offerId
        )
          ? "waitinglist"
          : "book",
      }))
  );

  useEffect(() => {
    if (open && !modalRef.current.open) {
      modalRef.current.showModal();
    }
  });

  let type = "";

  const conflict = form.resolve;

  if (conflict.isBookable && conflict.waitingList) {
    type = "waitingList";
  }

  if (!conflict.isBookable && !conflict.waitingList) {
    type = "notBookable";
  } else type = "conflictPartially";

  const buttonStyles = {
    minWidth: "fit-content",
    maxWidth: "12rem",
    width: "50%",
  };

  const selectedOffers = form.resolve.selected
    //Listen flach klopfen
    .flatMap((selectedItem) => selectedItem)
    // Eindeutige Werte finden
    .filter((offerId, index, self) => {
      return self.indexOf(offerId) === index;
    })
    // Offer zu id suchen
    .map((selectedOfferId) => findOffer(offers, selectedOfferId))
    // Nicht gefundene offer rausfilter
    .filter((offer) => !!offer)
    .map((offer) => {
      return {
        ...offer,
        participants: form.participants.filter((participant) =>
          participant.offers.includes(offer.id)
        ),
      };
    });

  const availability = (offer) => {
    console.log(offer);
    return offer.limit - offer.reservations > 0 ? (
      <span> freie Plätze: {offer.limit - offer.reservations}</span>
    ) : (
      <span>Warteliste: {offer.limitWaiting - offer.reservationsWaiting}</span>
    );
  };

  const date = useDate();
  const time = useTime();

  const handleClick = function (event, offer, participantIndex, type) {
    if (type == "waitinglist") {
      dispatch({
        type: "ADD_TO_WAITINGLIST",
        payload: { participantIndex, offer },
      });
    }

    if (type == "book") {
      dispatch({
        type: "ADD_OFFER",
        payload: { participantIndex, offer },
      });
    }

    if (type == "remove") {
      dispatch({
        type: "DELETE_OFFER",
        payload: { participantIndex, offer },
      });
    }
  };

  const validateInput = () => {
    selectionRef.current.forEach((ref) => {
      ref.element?.setCustomValidity("");
    });

    const validationErrors = form.resolve.conflicts
      .map((conflict) => {
        const selectionByOffer = selection.filter(
          (sel) => sel.offerId == conflict.offerId
        );

        return form.participants.map((participant, participantIndex) => {
          const bookedSelections = selectionByOffer.filter(
            (sel) =>
              sel.type == "book" && sel.participantIndex == participantIndex
          );
          const waitingSelections = selectionByOffer.filter(
            (sel) =>
              sel.type == "waitinglist" &&
              sel.participantIndex == participantIndex
          );

          const offer = offers.filter(
            (offer) => offer.id == conflict.offerId
          )[0];

          const availableWaitingListSlots = Math.max(
            0,
            offer.limitWaiting - offer.reservationsWaiting
          );
          const availableSlots = Math.max(0, conflict.available);

          if (bookedSelections.length > availableSlots) {
            const error = `Bitte wählen Sie mindestens ${Math.abs(
              conflict.available - bookedSelections.length
            )} Angebote ab.`;

            return {
              error,
              elements: bookedSelections.flatMap((sel) =>
                selectionRef.current
                  .filter(
                    (ref) =>
                      ref.offerId == sel.offerId &&
                      ref.type == sel.type &&
                      ref.participantIndex == participantIndex
                  )
                  .map((sel) => sel.element)
              ),
            };
          }

          if (waitingSelections.length > availableWaitingListSlots) {
            const error = `Bitte wählen Sie mindestens ${Math.abs(
              conflict.available - waitingSelections.length
            )} Angebote ab.`;

            return {
              error,
              elements: waitingSelections.flatMap((sel) =>
                selectionRef.current
                  .filter(
                    (ref) =>
                      ref.offerId == sel.offerId &&
                      ref.type == sel.type &&
                      ref.participantIndex == participantIndex
                  )
                  .map((sel) => sel.element)
              ),
            };
          }
        });
      })
      .flatMap((error) => error)
      .filter((error) => error);

    validationErrors.forEach((validationError) => {
      validationError.elements.forEach((element) => {
        element?.setCustomValidity(validationError.error);
      });
    });
  };

  const setupRef = (element, offerId, participantIndex, type) => {
    const refs = selectionRef.current;

    if (!element) {
      const item = refs.filter(
        (item) =>
          item.offerId == offerId &&
          participantIndex == participantIndex &&
          type == type
      )[0];

      refs.splice(refs.indexOf(item), 1);
    } else {
      refs.push({ element, offerId, participantIndex, type });
    }
  };

  const isChecked = (offerId, participantIndex, type) => {
    return selection.filter(
      (item) =>
        item.offerId == offerId &&
        item.participantIndex == participantIndex &&
        item.type == type
    ).length;
  };

  useEffect(() => {
    validateInput();
  });

  return (
    <dialog
      ref={modalRef}
      className={styles.Dialog}
      onClose={onClose}
      aria-modal="true"
    >
      <form>
        <div className={styles.DialogContainer}>
          <header className={styles.Header}>
            <h1 className={styles.Title}>Buchung nicht möglich</h1>
          </header>
          <div className={styles.Content}>
            <p>{conflict?.messages}</p>
            {type == "conflictPartially" && (
              <table className={styles.Table}>
                <thead>
                  <tr className={styles.OfferHeader}>
                    <th>Zeitraum</th>
                    <th>Angebot</th>
                    <th>Verfügbarkeit</th>
                    <th>Kind</th>
                    <th>buchen</th>
                    <th>Wartel.</th>
                    <th></th>
                  </tr>
                </thead>

                {selectedOffers.map((offer) => {
                  return (
                    <tbody>
                      {offer.participants.map((participant, participantIndex) =>
                        offerRow(
                          participantIndex,
                          offer,
                          date,
                          availability,
                          participant,
                          setupRef,
                          handleClick,
                          isChecked
                        )
                      )}
                    </tbody>
                  );
                })}
              </table>
            )}
            {children}
          </div>
          <footer className={styles.Footer}>
            {type == "notBookable" && (
              <Button
                style={buttonStyles}
                secondary
                onClick={() => modalRef.current.close()}
              >
                schließen
              </Button>
            )}
            {type == "waitingList" && (
              <>
                <Button
                  style={buttonStyles}
                  onClick={() => modalRef.current.close()}
                  secondary
                >
                  abbrechen
                </Button>
                <Button style={buttonStyles}>Warteliste</Button>
              </>
            )}
            {type == "conflictPartially" && (
              <>
                <Button
                  style={buttonStyles}
                  onClick={() => modalRef.current.close()}
                  secondary
                >
                  abbrechen
                </Button>
                <Button style={buttonStyles} type="submit">
                  Auswahl bestätigen
                </Button>
              </>
            )}
          </footer>
        </div>
      </form>
    </dialog>
  );
};

export default ConflictDialog;

function findOffer(offers, selectedOfferId) {
  return offers.filter((offer) => offer.id == selectedOfferId)[0];
}

function offerRow(
  participantIndex,
  offer,
  date,
  availability,
  participant,
  setupRef,
  handleClick,
  isChecked
) {
  return (
    <tr key={`${participantIndex}-${offer.id}`}>
      {participantIndex == 0 && (
        <>
          <th className={styles.RowGroupItem} rowSpan={999}>
            {date(offer.begin)} - {date(offer.end)}
          </th>
          <th className={styles.RowGroupItem} rowSpan={999}>
            {offer.name}
          </th>
          <th className={styles.RowGroupItem} rowSpan={999}>
            {availability(offer)}
          </th>
        </>
      )}
      <th>
        {participant.firstName} {participant.lastName}
      </th>

      <th className={styles.Choices}>
        <Radiobutton
          ref={(node) => setupRef(node, offer.id, participantIndex, "book")}
          onChange={(e) => handleClick(e, offer, participantIndex, "book")}
          value="buchen"
          name={`${participantIndex}-${offer.id}`}
          checked={isChecked(offer.id, participantIndex, "book")}
        ></Radiobutton>
      </th>
      <th className={styles.Choices}>
        <Radiobutton
          ref={(node) =>
            setupRef(node, offer.id, participantIndex, "waitinglist")
          }
          onChange={(e) =>
            handleClick(e, offer, participantIndex, "waitinglist")
          }
          value="warteliste"
          name={`${participantIndex}-${offer.id}`}
          checked={isChecked(offer.id, participantIndex, "waitinglist")}
        ></Radiobutton>
      </th>
      <th className={styles.Choices}>
        <Radiobutton
          ref={(node) => setupRef(node, offer.id, participantIndex, "remove")}
          onChange={(e) => handleClick(e, offer, participantIndex, "remove")}
          value="entfernen"
          name={`${participantIndex}-${offer.id}`}
          checked={isChecked(offer.id, participantIndex, "remove")}
        >
          entfernen
        </Radiobutton>
      </th>
    </tr>
  );
}
